import {
  boxResizeScales,
  maskRidesBox,
  resizedBakedMargins,
  scalePaths,
  scaleVectorParams,
} from '@lite-workspace/dto-fotoclass-templates';
import { SpreadData } from '../interfaces/spread-data.interface';
import { BoxPassthroughMeta, DrawableData, DrawableTextData, DrawableGroupData } from '../interfaces/drawable-data.interface';
import { RectangleData } from '../interfaces/rectangle-data.interface';
import { pinTextAnchorToFrame, textSatellitesForFrame } from '../text/text-satellites';
import {
  frameDelta,
  maskRectForFrame,
  rotatedFrameDelta,
  transformEffectOrigins,
  transformDrawableGradientEffect,
  transformLayerStyleMasks,
  turnableMaskRect,
} from '../geometry/frame-delta';

export interface DrawableRectUpdate {
  readonly parentTransform?: boolean;
  readonly id: string;
  readonly rect: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  /** ИТОГОВЫЙ угол элемента, а не приращение: ровно то, что отдаёт render-бокс (см. `visibleRotation`). */
  readonly rotation: number;
}

/**
 * Угол элемента хранится сплитом: `drawable.rotation` (layer-level) + `naturalAligns.container.angle`.
 * Старый путь рендера (canvas-engine `DrawableAdapter`) показывает их СУММУ, и жест снимает угол именно оттуда — то есть подаёт
 * абсолют. Persist-кодек custom-template-v1 схлопывает сплит в `drawable.rotation` и обнуляет `container.angle`
 * (`normalizer/boxes/rotation.ts`), поэтому у мигрированной книги весь угол лежит в первом слагаемом: запись
 * абсолюта в `container.angle` без оглядки на него давала 2θ на первом же drag/nudge/resize.
 *
 * Носителем правки остаётся `container.angle` — `drawable.rotation` не мутируется: его читают пути мимо персиста
 * (mirror-spread-layout, convertBoxType, spread-to-template), а неподвижная база держит `sameContainer` честным,
 * иначе align/distribute метили бы повёрнутый фон как `placed` на ride-along записи тем же углом.
 */
interface RotationCarrier {
  readonly rotation?: number;
  readonly naturalAligns?: { readonly container?: { readonly angle?: number } };
}

export function visibleRotation(drawable: RotationCarrier | null | undefined): number {
  return (drawable?.rotation ?? 0) + (drawable?.naturalAligns?.container?.angle ?? 0);
}

/**
 * Drawable-форма `resizedBakedMargins`: у декора поля лежат в `templateMeta.objectBox`, у рамки — в
 * `templateMeta.imageBox.decorSource`. Общий сток для ресайза рамки в альбоме и масштаба детей группы в раскладке.
 */
export function withResizedBakedMargins<T extends DrawableData>(drawable: T, sx: number, sy: number): T {
  const meta = drawable.templateMeta as BoxPassthroughMeta | undefined;
  if (!meta || (sx === 1 && sy === 1)) return drawable;
  if (drawable.type === 'object' && meta.objectBox) {
    const objectBox = resizedBakedMargins(meta.objectBox, sx, sy);
    return objectBox === meta.objectBox ? drawable : ({ ...drawable, templateMeta: { ...meta, objectBox } } as T);
  }

  if (drawable.type === 'image' && meta.imageBox) {
    const imageBox = resizedBakedMargins(meta.imageBox, sx, sy);
    return imageBox === meta.imageBox ? drawable : ({ ...drawable, templateMeta: { ...meta, imageBox } } as T);
  }

  return drawable;
}

function containerAngleFor(drawable: DrawableData, absoluteRotation: number): number {
  return absoluteRotation - ((drawable as { rotation?: number }).rotation ?? 0);
}

// The background rides here too: the editor lets it be dragged, and a geometry write the model refuses
// leaves the canvas showing a move nothing can store — and undo with nothing to take back.
const MOVABLE_TYPES: ReadonlySet<DrawableData['type']> = new Set(['text', 'image', 'vector', 'object', 'background']);

export interface DrawableRectWriteOptions {
  /** `stay`: a re-measured frame (not a gesture) — textPath.d/transform keep their place, only a boxTopLeft anchor pins. */
  readonly textSatellites?: 'follow' | 'stay';
}

export function updateDrawableRect(
  spread: SpreadData,
  updates: ReadonlyArray<DrawableRectUpdate>,
  options: DrawableRectWriteOptions = {},
): SpreadData {
  if (updates.length === 0) return spread;
  const updateById = new Map(updates.map(u => [u.id, u]));

  return {
    ...spread,
    collages: spread.collages.map(collage => ({
      ...collage,
      drawableObjects: collage.drawableObjects.map(d => applyUpdate(d, updateById, options)),
    })),
  } as SpreadData;
}

// `groupOrigin` — начало рамки ребёнка группы в кадре разворота: сумма начал групп над ним (рамки детей групп-локальные).
function applyUpdate(
  drawable: DrawableData,
  updateById: Map<string, DrawableRectUpdate>,
  options: DrawableRectWriteOptions,
  groupOrigin = { x: 0, y: 0 },
): DrawableData {
  if (drawable.type === 'group') {
    const group = drawable as DrawableGroupData;
    const { left, top } = group.naturalAligns.container;
    const origin = { x: groupOrigin.x + left, y: groupOrigin.y + top };
    return {
      ...group,
      drawableObjects: group.drawableObjects.map(child => applyUpdate(child, updateById, options, origin)),
    } as DrawableGroupData;
  }

  const update = updateById.get(drawable.id);
  if (!update || !MOVABLE_TYPES.has(drawable.type)) return drawable;
  if (options.textSatellites !== 'stay') {
    const old = drawable.naturalAligns.container;
    drawable = {
      ...drawable,
      ...transformEffectOrigins(
        drawable,
        rotatedFrameDelta(
          { x: old.left, y: old.top, width: old.width, height: old.height },
          update.rect,
          visibleRotation(drawable),
          update.rotation,
        ),
        update.parentTransform ? 'parent' : 'layer',
      ),
    };
  }

  if ('maskRect' in drawable && drawable.maskRect && (update.parentTransform || maskRidesBox(drawable.maskRect))) {
    const old = drawable.naturalAligns.container;
    const frame = { x: old.left, y: old.top, width: old.width, height: old.height };
    const angle = visibleRotation(drawable);
    drawable = {
      ...drawable,
      maskRect: maskRectForFrame(
        turnableMaskRect(drawable.maskRect, frame, angle, update.rotation),
        frame,
        update.rect,
        angle,
        update.rotation,
      ),
    };
  }

  if (drawable.layerStyleMasks && options.textSatellites !== 'stay') {
    const old = drawable.naturalAligns.container;
    drawable = {
      ...drawable,
      layerStyleMasks: transformLayerStyleMasks(
        drawable.layerStyleMasks,
        rotatedFrameDelta(
          { x: old.left, y: old.top, width: old.width, height: old.height },
          update.rect,
          visibleRotation(drawable),
          update.rotation,
        ),
        update.parentTransform ? 'parent' : 'layer',
      ),
    };
  }

  if (drawable.type !== 'text' && drawable.layerStyleRendering === 'live' && drawable.effects) {
    const old = drawable.naturalAligns.container;
    const delta = rotatedFrameDelta(
      { x: old.left, y: old.top, width: old.width, height: old.height },
      update.rect,
      visibleRotation(drawable),
      update.rotation,
    );
    drawable = {
      ...drawable,
      effects: drawable.effects.map(e => transformDrawableGradientEffect(e, delta, update.parentTransform ? 'parent' : 'layer')),
    };
  }

  // Контур, габариты, радиальная заливка и обводка вектора лежат в кадре его рамки: ручка размера,
  // растянувшая рамку, растягивает и фигуру, иначе старый контур остаётся внутри новой рамки. Габариты лежат в кадре
  // разворота и у ребёнка группы, поэтому обе рамки берутся там же: групп-локальная дельта сдвинула бы их на G·(1 − s).
  if (drawable.type === 'vector' && options.textSatellites !== 'stay') {
    const old = drawable.naturalAligns.container;
    const { x: gx, y: gy } = groupOrigin;
    const [sx, , , sy, dx, dy] = frameDelta(
      { x: old.left + gx, y: old.top + gy, width: old.width, height: old.height },
      { ...update.rect, x: update.rect.x + gx, y: update.rect.y + gy },
    );
    drawable = { ...drawable, vector: scaleVectorParams(drawable.vector, sx, sy, dx, dy) };
  }

  // Поля запечённых эффектов декора и отложенные поля рамки — пиксели вдоль осей рамки: ручка размера, растянувшая
  // плитку, растягивает и их (`resizedBakedMargins`), иначе обратный ход декор ↔ рамка срежет или вырастит не то.
  if (options.textSatellites !== 'stay') {
    const old = drawable.naturalAligns.container;
    const [sx, sy] = boxResizeScales(old, update.rect);
    drawable = withResizedBakedMargins(drawable, sx, sy);
    // A pack frame's contour is frame-local and the renderer only adds the rect origin: an unscaled one clips the
    // photo to the old size while the model holds the new rect (twin of `repositionDrawable`).
    if (drawable.type === 'image' && drawable.paths && (sx !== 1 || sy !== 1)) {
      drawable = { ...drawable, paths: scalePaths(drawable.paths, sx, sy, 0, 0) };
    }
  }

  const nextContainer = {
    left: update.rect.x,
    top: update.rect.y,
    width: update.rect.width,
    height: update.rect.height,
    angle: containerAngleFor(drawable, update.rotation),
  };

  if (drawable.type === 'text') {
    const text = drawable as DrawableTextData;
    const c = text.naturalAligns.container;
    const nextText =
      text.text &&
      (options.textSatellites === 'stay'
        ? pinTextAnchorToFrame(text.text, update.rect)
        : textSatellitesForFrame(text.text, { x: c.left, y: c.top, width: c.width, height: c.height }, update.rect));

    return {
      ...text,
      effects: text.effects?.map(effect =>
        options.textSatellites !== 'stay'
          ? transformDrawableGradientEffect(
              effect,
              rotatedFrameDelta(
                { x: c.left, y: c.top, width: c.width, height: c.height },
                update.rect,
                visibleRotation(text),
                update.rotation,
              ),
              update.parentTransform ? 'parent' : 'layer',
            )
          : effect,
      ),
      naturalAligns: { ...text.naturalAligns, container: nextContainer },
      text: nextText,
    };
  }

  // A MOVED background is a placement, and the full-bleed normalizer must stop reading it as a lost bleed.
  // The move has to be real: align/distribute write every box in the selection, unchanged ones included, so a
  // mark stamped on arrival would drop the page cover of a background nobody touched — visible only in print.
  const prior = (drawable as { naturalAligns?: { container?: RectangleData } }).naturalAligns?.container;
  const placed = drawable.type === 'background' && !(prior && sameContainer(prior, nextContainer)) ? { placed: true } : {};

  return {
    ...drawable,
    ...placed,
    naturalAligns: { ...(drawable as { naturalAligns: object }).naturalAligns, container: nextContainer },
  } as DrawableData;
}

// The model arrives as untyped JSON — the angle may be missing, and then it is zero.
function sameContainer(a: RectangleData, b: RectangleData): boolean {
  return (
    a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height && (a.angle ?? 0) === (b.angle ?? 0)
  );
}
